import { avatarColors, colors } from '../styles/tokens';
import type { TiendaVendedor } from '../types/indicadores.types';
import { ring } from './format';
import { semaforo } from './glosario';

/**
 * Circunferencia del anillo de las cards de vendedor (`r=26`, línea 254 del
 * handoff: `vc=163.363`). Se calcula con `2*PI*r` en vez de hardcodear el
 * literal del handoff, igual que `BodegaCard` (`RING_CIRC = 2*Math.PI*44`).
 */
export const VENDEDOR_RING_CIRC = 2 * Math.PI * 26;

/**
 * 2026-08-03 (punto 1 del usuario): se RETIRA el tope de 8 cards que traía el
 * brief original ("cap at ~8"). El usuario reportó ver 7 tarjetas de una
 * plantilla de 10 y pidió que estén "todas las tarjetas siempre visibles":
 * con el backend devolviendo ya la UNIÓN (quien usó la herramienta ∪ quien
 * tiene venta en Proscai), recortar aquí volvía a esconder gente. Se renderiza
 * la lista completa tal como la manda el backend, ya ordenada por uso.
 */

/** Colores de badge oro/plata/bronce por rank (línea 442 del handoff: `RANK`). */
const RANK_COLORS: { c: string; bg: string }[] = [
  { c: '#8a6d12', bg: '#fbf3dd' }, // #1 oro
  { c: '#6b7280', bg: '#eef0f4' }, // #2 plata
  { c: '#9a5f34', bg: '#f7ece2' }, // #3 bronce
];
/** Badge de rank #4 en adelante (sin color especial). */
const RANK_DEFAULT = { c: '#8b909b', bg: '#eef0f4' };

/**
 * Iniciales del avatar: primera letra de las 2 primeras palabras de
 * `nombre` (ej. "Laura Méndez" -> "LM"). Si `nombre` es `null` (vendedor sin
 * nombre resuelto — ver alta por número de empleado, `MEMORY.md`), cae a los
 * primeros 2 caracteres de `externalUserId`.
 */
export function buildInitials(nombre: string | null, externalUserId: string): string {
  if (nombre) {
    const parts = nombre.trim().split(/\s+/).filter(Boolean);
    if (parts.length > 0) {
      const first = parts[0][0] ?? '';
      const second = parts.length > 1 ? parts[1][0] ?? '' : '';
      return (first + second).toUpperCase();
    }
  }
  return externalUserId.slice(0, 2).toUpperCase();
}

/**
 * Tarjeta de vendedor ya derivada (todo lo que `components/tienda/Vendedores`
 * necesita para pintar sin recalcular nada).
 *
 * Ajuste A (2026-07-22, diseño en
 * `cdr-sa-deploy/docs/specs/2026-07-22-tablero-ajustes-vendedor-negados-tienda-design.md`):
 * se QUITA el chip de texto "Estable"/"Necesita apoyo" (`tag`) y el banner de
 * coaching (`buildInsight`, eliminado de este módulo). `ringColor` se
 * conserva SOLO como color del trazo del anillo de efectividad (referencia
 * secundaria en la card) — ya no colorea ningún chip de texto. Se agrega
 * `negados` (Actividad: consultas/surtió/negados).
 *
 * `ventaPares`/`ventaContado`/`ventaCredito`/`clientesNuevos` se propagan tal
 * cual desde `TiendaVendedor` — son PARES, nunca montos $.
 *
 * Ajuste B1 (2026-07-22, plan `2026-07-22-warm-scheduler-y-tarjeta-vendedor-v2`):
 * se agregan los derivados de la tarjeta v2 — `codigo` (AGNUM/fallback),
 * `pendiente`/`efectividad` (Zona 2 Actividad) y los anchos de barra `pct*`
 * (0..100, Zona 2 Actividad + Zona 3 Venta) para que el componente pinte sin
 * recalcular nada. `ringColor`/`pctStr`/`ringDash` se conservan para no
 * romper más de lo necesario el shape existente, pero ahora se derivan de
 * `efectividad` (entero 0..100) en vez de `pctEfectividad*100` vs `goal`.
 */
export interface VendedorCard {
  externalUserId: string;
  codigo: string;
  name: string;
  initials: string;
  avatarColor: string;
  ringColor: string;
  ringDash: string;
  pctStr: string;
  consultas: number;
  atendidas: number;
  negados: number;
  pendiente: number;
  /** Entero 0..100: S ÷ C si la API manda `solicitudes`; si no, A ÷ C. */
  efectividad: number;
  /**
   * `true` = `efectividad` es S ÷ C y se llama «Efectividad de inventario», como
   * en la tienda y en Global. `false` = API anterior sin solicitudes por
   * vendedor: es A ÷ C y se llama «Consultas atendidas». Nunca dos fórmulas con
   * el mismo nombre (glosario, 12-oct).
   */
  efectividadEsSC: boolean;
  aroColor: string;
  pctSurtio: number;
  pctNegado: number;
  pctPendiente: number;
  pctContado: number;
  pctCredito: number;
  ventaPares: number;
  ventaContado: number;
  ventaCredito: number;
  /** Monto $ (bruto del ticket, DBRUTO por documento) de contado/crédito. Nuevo 2026-07-28. */
  ventaContadoMonto: number;
  ventaCreditoMonto: number;
  /** Préstamos personales: NO son venta comercial, no están en los montos. */
  ventaPrestamos?: number;
  /**
   * Señal "vendió sin pasar por la herramienta" (2026-07-28, punto 3 del
   * usuario): pares vendidos por encima de lo que bodega SURTIÓ vía la
   * herramienta = `max(0, ventaPares − atendidas)`. Cuando es `> 0`, la card
   * lo pinta en ROJO ("N pares vendidos sin solicitar por la herramienta") —
   * es el mismo fenómeno que `tasaAtencion > 100%`, pero en pares absolutos.
   */
  ventaFueraHerramienta: number;
  clientesNuevos: number;
  /** Créditos ESTRENADOS con el vendedor (regla B, 5-oct-2026). Puede ser mayor que `clientesNuevos`. */
  clientesNuevosUsaron: number;
  /**
   * Ajuste 2026-07-22 (plan `2026-07-22-tarjeta-vendedor-tasa-y-trazabilidad-plan`,
   * Task FE-A, decisión del usuario): Tasa de atención = vendido ÷ surtido —
   * de lo que bodega surtió (`atendidas`), cuánto se convirtió en venta
   * (`ventaPares`). Puede pasar de 100% (venta de piso sin request a bodega).
   * `null` cuando `atendidas === 0` (sin denominador, evita `NaN`/división
   * por cero) — el componente pinta "—%" en ese caso.
   */
  tasaAtencion: number | null;
  /**
   * 2026-08-03 (punto 1 del usuario): el vendedor VENDIÓ en el periodo pero no
   * registró ni una consulta en CADER. La card lo marca en vez de esconderlo —
   * antes esta gente simplemente no aparecía (la lista salía solo de eventos) y
   * su venta caía en "mostrador", lo que hacía que el mostrador se viera enorme
   * (36,5% en AZCAPO) sin serlo.
   */
  sinUsoDelSistema: boolean;
  rank: number;
  rankLabel: string;
  rankColor: string;
  rankBg: string;
  cardBorder: string;
}

/** Anchos de barra 0..100 (0 si `total` es 0 — evita `NaN`). */
function pct(n: number, total: number): number {
  return total > 0 ? (n / total) * 100 : 0;
}

/**
 * Deriva las cards del ranking de vendedores (líneas 442-457 del handoff).
 * El ORDEN es por **uso (consultas)**, ya resuelto por el backend — `rank`
 * es solo `index+1`, esta función NO re-ordena ni recalcula el orden.
 *
 * Ajuste B1 (tarjeta v2, spec §3): `efectividad` = `atendidas/consultas`
 * redondeado a entero (0 si `consultas` es 0, nunca `NaN`) — reemplaza el
 * viejo `pctEfectividad*100` como fuente del aro y del label. `aroColor` usa
 * los umbrales 60/50 del spec §6 (`>=60` verde `serie.surtio`, `>=50` ámbar
 * `serie.aroMedio`, si no rojo `serie.negado`) — YA NO depende de `goal`.
 * `ringColor`/`ringDash`/`pctStr` se conservan por compatibilidad (algunos
 * consumidores viejos) pero ahora se derivan de `efectividad`/`aroColor`.
 *
 * `_goal` queda sin usar (el umbral ahora es fijo 60/50, no configurable) —
 * se conserva el parámetro para no romper la firma que consume
 * `Vendedores.tsx` hasta que B2 (rediseño del componente) lo retire.
 */
export function buildVendedorCards(vendedores: TiendaVendedor[], _goal: number): VendedorCard[] {
  return vendedores.map((v, i) => {
    const { consultas, atendidas, negados } = v;
    const pendiente = Math.max(0, consultas - atendidas - negados);
    // 12-oct: con `solicitudes` (la API las manda desde ese día) la tarjeta
    // muestra S ÷ C, la efectividad de inventario de la tienda y de Global, con
    // su color (el azul de marca: no tiene semáforo). Sin ellas, A ÷ C con los
    // umbrales 60/50 de siempre, y otro nombre.
    const efectividadEsSC = typeof v.solicitudes === 'number';
    const numerador = efectividadEsSC ? (v.solicitudes as number) : atendidas;
    const efectividad = consultas > 0 ? Math.round((numerador / consultas) * 100) : 0;
    const aroColor = efectividadEsSC
      ? semaforo('efectividad', consultas > 0 ? numerador / consultas : null)
      : efectividad >= 60
        ? colors.serie.surtio
        : efectividad >= 50
          ? colors.serie.aroMedio
          : colors.serie.negado;
    const paresTotal = v.ventaContado + v.ventaCredito;
    // Tasa de atención = vendido ÷ surtido (ver comentario del campo en `VendedorCard`).
    const tasaAtencion = v.atendidas > 0 ? Math.round((v.ventaPares / v.atendidas) * 100) : null;
    const rank = RANK_COLORS[i] ?? RANK_DEFAULT;

    return {
      externalUserId: v.externalUserId,
      codigo: v.codigo || v.externalUserId,
      name: v.nombre ?? v.externalUserId,
      initials: buildInitials(v.nombre, v.externalUserId),
      avatarColor: avatarColors[i % avatarColors.length],
      ringColor: aroColor,
      ringDash: ring(efectividad, VENDEDOR_RING_CIRC),
      pctStr: `${efectividad}%`,
      consultas,
      atendidas,
      negados,
      pendiente,
      efectividad,
      efectividadEsSC,
      aroColor,
      pctSurtio: pct(atendidas, consultas),
      pctNegado: pct(negados, consultas),
      pctPendiente: pct(pendiente, consultas),
      pctContado: pct(v.ventaContado, paresTotal),
      pctCredito: pct(v.ventaCredito, paresTotal),
      ventaPares: v.ventaPares,
      ventaContado: v.ventaContado,
      ventaCredito: v.ventaCredito,
      ventaContadoMonto: v.ventaContadoMonto,
      ventaCreditoMonto: v.ventaCreditoMonto,
      ventaPrestamos: v.ventaPrestamos,
      ventaFueraHerramienta: Math.max(0, v.ventaPares - v.atendidas),
      clientesNuevos: v.clientesNuevos,
      clientesNuevosUsaron: v.clientesNuevosUsaron,
      tasaAtencion,
      // El backend ya lo resuelve (`consultas === 0`); se respeta tal cual y
      // sólo se cae al cálculo local si un consumidor viejo no lo manda.
      sinUsoDelSistema: v.sinUsoDelSistema ?? consultas === 0,
      rank: i + 1,
      rankLabel: `#${i + 1}`,
      rankColor: rank.c,
      rankBg: rank.bg,
      cardBorder: i === 0 ? colors.rankOro : colors.border,
    };
  });
}
